import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Grid,
  Input,
  InputNumber,
  message,
  Modal,
  Popover,
  Progress,
  Row,
  Dropdown,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  Empty,
} from "antd";
import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  FileSearchOutlined,
  FileImageOutlined,
  MoreOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
  DollarOutlined,
  PauseOutlined,
  PlayCircleOutlined,
  ScheduleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import CreditFormModal from "./CreditFormModal";
import CreditDetailsDrawer from "./CreditDetailsDrawer";
import CreditImageImportModal from "./CreditImageImportModal";
import ManualPaymentsModal, {
  ManualPaymentRowInput,
} from "./ManualPaymentsModal";
import CreditWithRatesModal, {
  CreditWithRatesSubmit,
} from "./CreditWithRatesModal";
import {
  addManualPayment,
  createCredit,
  deleteCredit,
  deletePayment,
  fetchCreditDetails,
  fetchCredits,
  fetchLocations,
  freezeCredit,
  markInstallmentPaid,
  updateCredit,
  updatePayment,
} from "./creditsService";
import { ClientLocation, Credit, CreditPayment } from "./types";
import { ExchangeRate, getExchangeRates } from "../../utils/api/exchangeRateAPI";
import "./Credits.scss";

dayjs.extend(isSameOrBefore);

const { Title } = Typography;
const { useBreakpoint } = Grid;

type RSDBreakdownRow = {
  name: string;
  due: number;
  currency: string;
  amount: number;
  rate: number;
  rateMissing: boolean;
};

// Sadržaj računice (originalni iznos i valuta × kurs = RSD), deljen između
// Popover-a na desktopu (hover) i Modal-a na tabletu/mobilnom (dugme "Detalji").
const RSDBreakdownContent: React.FC<{
  breakdown: RSDBreakdownRow[];
  rateMissing: boolean;
  t: (key: string, opts?: any) => any;
}> = ({ breakdown, rateMissing, t }) => (
  <div style={{ maxWidth: 320 }}>
    <Space direction="vertical" style={{ width: "100%" }} size={4}>
      {breakdown.map((row, idx) => (
        <div
          key={idx}
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 8,
            fontSize: 12,
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {row.name}
          </span>
          <span style={{ whiteSpace: "nowrap" }}>
            {row.due.toLocaleString(undefined, { maximumFractionDigits: 2 })}{" "}
            {row.currency}
            {row.currency !== "RSD" && (
              <>
                {" × "}
                {row.rateMissing ? "?" : row.rate.toLocaleString()}
                {" = "}
                <strong>{Math.round(row.amount).toLocaleString("sr-RS")} RSD</strong>
                {row.rateMissing && " ⚠"}
              </>
            )}
          </span>
        </div>
      ))}
    </Space>
    {rateMissing && (
      <div style={{ marginTop: 8, fontSize: 12, color: "#faad14" }}>
        {t(
          "Exchange rate not set for one or more currencies — using 1:1. Set the rate in Settings.",
        )}
      </div>
    )}
  </div>
);

// Statistic kartica koja, kad je osnovna valuta klijenta RSD i postoje krediti
// u stranoj valuti, prikazuje ukupan iznos preračunat u dinare. Računica
// (originalni iznos × kurs = RSD) je dostupna na hover-u na desktopu (Popover),
// a na tabletu/mobilnom preko dugmeta "Detalji" koje otvara Modal - hover ne
// postoji na dodir ekranima pa je eksplicitno dugme praktičnije za taj UI/UX.
// Za klijente čija osnovna valuta nije RSD, ili kad nema šta da se preračuna,
// ponaša se kao obična statistika u klijentovoj valuti (bez konverzije).
const CurrencyStatCard: React.FC<{
  title: string;
  isRSDBase: boolean;
  breakdown: RSDBreakdownRow[];
  rawValue: number;
  currencySymbol: string;
  color?: string;
  rawPrefix?: boolean;
  isMobile: boolean;
  t: (key: string, opts?: any) => any;
}> = ({
  title,
  isRSDBase,
  breakdown,
  rawValue,
  currencySymbol,
  color,
  rawPrefix,
  isMobile,
  t,
}) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const showRSD = isRSDBase && breakdown.length > 0;
  const rateMissing = breakdown.some((row) => row.rateMissing);
  const rsdValue = breakdown.reduce((sum, row) => sum + row.amount, 0);

  if (!showRSD) {
    return (
      <Card>
        <Statistic
          title={title}
          value={rawValue}
          precision={2}
          valueStyle={color ? { color } : undefined}
          {...(rawPrefix ? { prefix: currencySymbol } : { suffix: currencySymbol })}
        />
      </Card>
    );
  }

  const statistic = (
    <Statistic
      title={title}
      value={Math.round(rsdValue)}
      valueStyle={color ? { color } : undefined}
      suffix={
        rateMissing ? (
          <span>
            RSD <Tag color="warning">⚠</Tag>
          </span>
        ) : (
          "RSD"
        )
      }
      formatter={(val) => Number(val).toLocaleString("sr-RS")}
    />
  );

  if (isMobile) {
    return (
      <>
        <Card>
          {statistic}
          <Button
            type="link"
            size="small"
            icon={<InfoCircleOutlined />}
            onClick={() => setDetailsOpen(true)}
            style={{ padding: 0, marginTop: 4, height: "auto" }}
          >
            {t("Details")}
          </Button>
        </Card>
        <Modal
          open={detailsOpen}
          title={title}
          onCancel={() => setDetailsOpen(false)}
          footer={null}
        >
          <RSDBreakdownContent breakdown={breakdown} rateMissing={rateMissing} t={t} />
        </Modal>
      </>
    );
  }

  return (
    <Popover
      title={t("Calculation (foreign currency → RSD)")}
      content={<RSDBreakdownContent breakdown={breakdown} rateMissing={rateMissing} t={t} />}
    >
      <Card>{statistic}</Card>
    </Popover>
  );
};

const Credits: React.FC = () => {
  const { t } = useTranslation();
  const userData = useSelector((state: any) => state.userData.user);
  const storedUser = React.useMemo(() => {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      console.error("Failed to parse stored user", error);
      return null;
    }
  }, []);

  const clientId =
    (typeof userData?.clientId === "object"
      ? userData?.clientId?._id
      : userData?.clientId) ||
    (typeof storedUser?.clientId === "object"
      ? storedUser?.clientId?._id
      : storedUser?.clientId);

  const token = userData?.accessToken || storedUser?.accessToken;
  const getCurrencySymbol = () => {
    const currency =
      userData?.client?.currency ||
      userData?.clientId?.currency ||
      storedUser?.client?.currency ||
      storedUser?.clientId?.currency;
    if (currency === "Dinar") return "RSD";
    if (currency === "Euro") return "€";
    if (currency === "Dollar") return "$";
    return "€";
  };
  const currencySymbol = getCurrencySymbol();
  const isRSDBase = currencySymbol === "RSD";

  const [credits, setCredits] = useState<Credit[]>([]);
  const [locations, setLocations] = useState<ClientLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingCredit, setSavingCredit] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingCredit, setEditingCredit] = useState<Credit | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedCredit, setSelectedCredit] = useState<Credit | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [paymentModal, setPaymentModal] = useState<{
    open: boolean;
    credit?: Credit | null;
    payment?: CreditPayment | null;
  }>({ open: false });
  const [paymentForm] = Form.useForm<{
    amount?: number;
    paymentDate?: dayjs.Dayjs;
    note?: string;
  }>();
  const [savingPayment, setSavingPayment] = useState(false);
  const [bulkPaymentModal, setBulkPaymentModal] = useState<{
    open: boolean;
    credit?: Credit | null;
  }>({ open: false });
  const [savingBulkPayment, setSavingBulkPayment] = useState(false);
  const [creditWithRatesOpen, setCreditWithRatesOpen] = useState(false);
  const [savingCreditWithRates, setSavingCreditWithRates] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [locationFilter, setLocationFilter] = useState<string | null>(null);
  const [monthFilter, setMonthFilter] = useState<Dayjs | null>(dayjs());
  const [includeFrozen, setIncludeFrozen] = useState(false);
  const [imageImportOpen, setImageImportOpen] = useState(false);
  const [exchangeRates, setExchangeRates] = useState<ExchangeRate[]>([]);

  // Responsive breakpoints
  const screens = useBreakpoint();
  const isMobile = !screens.md; // < 768px
  // Tablet i mobilni nemaju hover, pa statistike sa računicom (EUR → RSD)
  // tu prikazuju dugme "Detalji" umesto oslanjanja na hover Popover.
  const isTouchStatsLayout = !screens.lg; // < 992px

  const loadCredits = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const data = await fetchCredits(clientId, token, includeFrozen);
      setCredits(data);
    } catch (error) {
      console.error(error);
      message.error(t("Failed to load credits"));
    } finally {
      setLoading(false);
    }
  }, [clientId, token, includeFrozen, t]);

  const loadLocations = useCallback(async () => {
    if (!clientId) return;
    try {
      const data = await fetchLocations(clientId, token);
      setLocations(data);
    } catch (error) {
      console.error(error);
    }
  }, [clientId, token]);

  const loadExchangeRates = useCallback(async () => {
    if (!clientId) return;
    try {
      const data = await getExchangeRates(clientId, token);
      setExchangeRates(data);
    } catch (error) {
      console.error(error);
    }
  }, [clientId, token]);

  useEffect(() => {
    if (clientId && token) {
      loadCredits();
      loadLocations();
      loadExchangeRates();
      // Emit event to refresh unpaid count in sidebar when entering credits page
      window.dispatchEvent(new CustomEvent("credits-updated"));
    }
  }, [clientId, token, loadCredits, loadLocations, loadExchangeRates]);

  // Kursevi za preračun u RSD (dinare) - mapira valutu kredita na kurs za RSD,
  // bez obzira na osnovnu valutu klijenta (RSD je uvijek cilj konverzije ovde).
  const ratesToRSD = useMemo(() => {
    const map: Record<string, number> = {};
    exchangeRates.forEach((r) => {
      if (r.toCurrency === "RSD" && r.fromCurrency !== "RSD") {
        map[r.fromCurrency] = r.rate;
      } else if (r.fromCurrency === "RSD" && r.toCurrency !== "RSD" && r.rate > 0) {
        map[r.toCurrency] = 1 / r.rate;
      }
    });
    return map;
  }, [exchangeRates]);

  const convertToRSD = useCallback(
    (amount: number, currency?: string) => {
      const cur = currency || "EUR";
      if (cur === "RSD") return { amount, rate: 1, rateMissing: false };
      const rate = ratesToRSD[cur];
      if (!rate) return { amount, rate: 1, rateMissing: true };
      return { amount: amount * rate, rate, rateMissing: false };
    },
    [ratesToRSD],
  );

  // Gradi listu stavki (po kreditu) sa originalnim iznosom/valutom i preračunom
  // u RSD, za prikaz u popover-u kraj svake statistike (Ukupno, Kamata, Plaćeno...).
  const buildRsdBreakdown = useCallback(
    (list: Credit[], amountFn: (c: Credit) => number): RSDBreakdownRow[] => {
      return list
        .map((c) => {
          const due = amountFn(c);
          if (!due) return null;
          const currency = c.currency || "EUR";
          const { amount, rate, rateMissing } = convertToRSD(due, currency);
          return { name: c.name, due, currency, amount, rate, rateMissing };
        })
        .filter((row): row is RSDBreakdownRow => !!row);
    },
    [convertToRSD],
  );

  const upsertCredit = (updated: Credit) => {
    setCredits((prev) => {
      const exists = prev.some((cr) => cr._id === updated._id);
      if (exists) {
        return prev.map((cr) =>
          cr._id === updated._id ? { ...cr, ...updated } : cr,
        );
      }
      return [updated, ...prev];
    });
  };

  const removeCredit = (id: string) => {
    setCredits((prev) => prev.filter((cr) => cr._id !== id));
    if (selectedCredit?._id === id) {
      setSelectedCredit(null);
      setDetailsOpen(false);
    }
  };

  const handleSaveCredit = async (values: any) => {
    if (!clientId) {
      message.error(t("Client is missing"));
      return;
    }
    setSavingCredit(true);
    try {
      const payload = {
        ...values,
        clientId,
        startDate: values.startDate?.toISOString(),
        locationId: values.locationId || null,
      };
      let saved: Credit;
      if (editingCredit) {
        saved = await updateCredit(editingCredit._id, payload, token);
        message.success(t("Credit updated"));
      } else {
        saved = await createCredit(payload, token);
        message.success(t("Credit created"));
      }
      upsertCredit(saved);
      setFormOpen(false);
      setEditingCredit(null);
      // Resetuj formu nakon kreiranja
      if (!editingCredit) {
        // Form će biti resetovan preko useEffect u CreditFormModal kada se otvori ponovo
      }
    } catch (error) {
      console.error(error);
      message.error(t("Failed to save credit"));
    } finally {
      setSavingCredit(false);
    }
  };

  const loadCreditDetails = async (credit: Credit) => {
    setSelectedCredit(credit);
    setDetailsOpen(true);
    setDetailsLoading(true);
    try {
      const data = await fetchCreditDetails(credit._id, token);
      setSelectedCredit(data);
      upsertCredit(data);
    } catch (error) {
      console.error(error);
      message.error(t("Failed to load credit details"));
    } finally {
      setDetailsLoading(false);
    }
  };

  const filteredCredits = useMemo(() => {
    return credits.filter((c) => {
      const matchesLocation =
        !locationFilter ||
        c.locationId === locationFilter ||
        (typeof c.locationId === "object" &&
          c.locationId?._id === locationFilter);
      return matchesLocation;
    });
  }, [credits, locationFilter]);

  const calculateDueForMonth = (credit: Credit, month: Dayjs | null) => {
    const selected = month || dayjs();
    const start = dayjs(credit.startDate).startOf("month");
    if (selected.isBefore(start)) return 0;

    // Za kredite sa stvarnim rasporedom uplata (bankarski obračun ili ručno
    // unete rate iz "Novi kredit sa ratama"), uzmi stvarni iznos iz payments
    // za izabrani mesec - bez obzira na paymentType (MANUAL ili INSTALLMENTS)
    if (
      credit.payments &&
      credit.payments.length > 0 &&
      (credit.useBankCalculation || credit.paymentType === "MANUAL")
    ) {
      const monthStart = selected.startOf("month");
      const monthEnd = selected.endOf("month");

      const monthPayments = credit.payments.filter((p) => {
        const paymentDate = dayjs(p.paymentDate);
        return (
          !paymentDate.isBefore(monthStart) && !paymentDate.isAfter(monthEnd)
        );
      });

      return monthPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
    }

    if (credit.paymentType !== "INSTALLMENTS") return 0;

    // Za obične kredite, koristi staru logiku
    const installmentIndex = selected.diff(start, "month");
    const totalInstallments = credit.installmentsCount || 0;
    if (installmentIndex < 0 || installmentIndex >= totalInstallments) return 0;

    const baseMonthly =
      credit.monthlyAmount ||
      (credit.totalWithInterest || credit.totalAmount || 0) /
        Math.max(1, totalInstallments);

    const remaining =
      (credit.totalWithInterest || credit.totalAmount || 0) -
      (credit.paidAmount || 0);

    return remaining > 0 ? Math.min(baseMonthly, remaining) : 0;
  };

  const monthlyDue = useMemo(() => {
    const month = monthFilter || dayjs();
    const activeCredits = filteredCredits.filter(
      (c) => (c.status || "ACTIVE") === "ACTIVE",
    );
    return activeCredits.reduce(
      (sum, c) => sum + calculateDueForMonth(c, month),
      0,
    );
  }, [filteredCredits, monthFilter]);

  // Preračun ukupnih rata za izabrani mesec u dinare (RSD), po svakom kreditu
  // pojedinačno prema njegovoj valuti (npr. EUR) i sačuvanom kursu.
  const monthlyDueRSDBreakdown = useMemo(() => {
    const month = monthFilter || dayjs();
    const activeCredits = filteredCredits.filter(
      (c) => (c.status || "ACTIVE") === "ACTIVE",
    );
    return buildRsdBreakdown(activeCredits, (c) => calculateDueForMonth(c, month));
  }, [filteredCredits, monthFilter, buildRsdBreakdown]);

  const handleOpenPaymentModal = (
    credit: Credit,
    payment?: CreditPayment | null,
  ) => {
    if (payment) {
      setPaymentModal({ open: true, credit, payment });
      paymentForm.setFieldsValue({
        amount: payment.amount,
        note: payment.note,
        paymentDate: payment.paymentDate
          ? dayjs(payment.paymentDate)
          : undefined,
      });
    } else {
      setBulkPaymentModal({ open: true, credit });
    }
  };

  const handleSaveBulkPayments = async (rows: ManualPaymentRowInput[]) => {
    const credit = bulkPaymentModal.credit;
    if (!credit || !rows.length) return;
    setSavingBulkPayment(true);
    try {
      let updated: Credit | null = null;
      for (const row of rows) {
        updated = await addManualPayment(
          credit._id,
          row.amount,
          token,
          row.note,
          row.paymentDate,
        );
      }
      if (updated) {
        upsertCredit(updated);
        if (selectedCredit?._id === updated._id) setSelectedCredit(updated);
      }
      message.success(rows.length > 1 ? t("Payments added") : t("Payment added"));
      setBulkPaymentModal({ open: false });
    } catch (error) {
      console.error(error);
      message.error(t("Failed to save payment"));
    } finally {
      setSavingBulkPayment(false);
    }
  };

  const handleCreateCreditWithRates = async (
    payload: CreditWithRatesSubmit,
  ) => {
    if (!clientId) {
      message.error(t("Client is missing"));
      return;
    }
    setSavingCreditWithRates(true);
    try {
      let credit = await createCredit(
        {
          ...payload.credit,
          clientId,
          paymentType: "MANUAL",
          paymentMode: "MANUAL",
          allowManualOverride: true,
          useBankCalculation: false,
        } as any,
        token,
      );

      for (const row of payload.rates) {
        credit = await addManualPayment(
          credit._id,
          row.amount,
          token,
          row.note,
          row.paymentDate,
        );
      }

      upsertCredit(credit);
      message.success(t("Credit created"));
      setCreditWithRatesOpen(false);
    } catch (error) {
      console.error(error);
      message.error(t("Failed to save credit"));
    } finally {
      setSavingCreditWithRates(false);
    }
  };

  const handleSavePayment = async () => {
    const credit = paymentModal.credit;
    if (!credit) return;
    try {
      const values = await paymentForm.validateFields();
      setSavingPayment(true);
      let updated: Credit;
      if (paymentModal.payment?._id) {
        updated = await updatePayment(
          credit._id,
          paymentModal.payment._id,
          {
            amount: values.amount,
            note: values.note,
            paymentDate: values.paymentDate?.toISOString(),
          },
          token,
        );
        message.success(t("Payment updated"));
      } else {
        updated = await addManualPayment(
          credit._id,
          values.amount || 0,
          token,
          values.note,
          values.paymentDate?.toISOString(),
        );
        message.success(t("Payment added"));
      }
      upsertCredit(updated);
      if (selectedCredit?._id === updated._id) setSelectedCredit(updated);
      setPaymentModal({ open: false });
    } catch (error) {
      console.error(error);
      message.error(t("Failed to save payment"));
    } finally {
      setSavingPayment(false);
    }
  };

  const handleMarkInstallment = async (creditId: string) => {
    setDetailsLoading(true);
    try {
      const updated = await markInstallmentPaid(creditId, token);
      upsertCredit(updated);
      if (selectedCredit?._id === updated._id) setSelectedCredit(updated);
      message.success(t("Installment marked as paid"));
    } catch (error) {
      console.error(error);
      message.error(t("Failed to mark installment"));
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleDeletePayment = async (creditId: string, paymentId: string) => {
    Modal.confirm({
      title: t("Delete payment?"),
      onOk: async () => {
        try {
          const updated = await deletePayment(creditId, paymentId, token);
          upsertCredit(updated);
          if (selectedCredit?._id === updated._id) setSelectedCredit(updated);
          message.success(t("Payment deleted"));
        } catch (error) {
          console.error(error);
          message.error(t("Failed to delete payment"));
        }
      },
    });
  };

  const handleConfirmPayment = async (
    creditId: string,
    paymentId: string,
    isPaid: boolean,
  ) => {
    try {
      const { confirmPaymentPaid } = await import("./creditsService");
      const updated = await confirmPaymentPaid(
        creditId,
        paymentId,
        isPaid,
        token,
      );
      upsertCredit(updated);
      if (selectedCredit?._id === updated._id) setSelectedCredit(updated);
      message.success(
        isPaid
          ? t("Payment confirmed as received")
          : t("Payment marked as not received"),
      );

      // Emit event to refresh unpaid credits count in sidebar
      window.dispatchEvent(new CustomEvent("credits-updated"));
    } catch (error) {
      console.error(error);
      message.error(t("Failed to update payment status"));
    }
  };

  const handleFreezeCredit = async (credit: Credit, freeze: boolean) => {
    try {
      const updated = await freezeCredit(credit._id, freeze, token);
      upsertCredit(updated);
      if (selectedCredit?._id === updated._id) setSelectedCredit(updated);
      message.success(freeze ? t("Credit frozen") : t("Credit unfrozen"));

      // Ako smo zamrzli kredit i ne prikazujemo zamrznute, refresh listu
      if (freeze && !includeFrozen) {
        loadCredits();
      }
    } catch (error) {
      console.error(error);
      message.error(t("Failed to update credit"));
    }
  };

  const handleDeleteCredit = (credit: Credit) => {
    Modal.confirm({
      title: t("Delete this credit plan?"),
      content: t("All related payments will also be removed."),
      okButtonProps: { danger: true, loading: deletingId === credit._id },
      onOk: async () => {
        setDeletingId(credit._id);
        try {
          await deleteCredit(credit._id, token);
          removeCredit(credit._id);
          message.success(t("Credit removed"));
        } catch (error) {
          console.error(error);
          message.error(t("Failed to delete credit"));
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  const stats = useMemo(() => {
    const total = credits.reduce(
      (acc, c) => acc + (c.totalWithInterest || c.totalAmount || 0),
      0,
    );
    const interest = credits.reduce(
      (acc, c) => acc + (c.interestAmount || 0),
      0,
    );
    const paid = credits.reduce((acc, c) => acc + (c.paidAmount || 0), 0);
    // Koristi remainingAmount iz schedule-a, ne računaj total - paid
    const remaining = credits.reduce(
      (acc, c) => acc + (c.remainingAmount || 0),
      0,
    );
    const active = credits.filter((c) => c.status !== "COMPLETED").length;
    return { total, interest, paid, remaining, active };
  }, [credits]);

  // Preračun statistika (ukupan obim, kamata, plaćeno, preostalo) u RSD, po
  // svakom kreditu pojedinačno prema njegovoj valuti - koristi se za prikaz
  // "originalni iznos × kurs = RSD" na svakoj kartici kad je osnovna valuta RSD.
  const totalRSDBreakdown = useMemo(
    () => buildRsdBreakdown(credits, (c) => c.totalWithInterest || c.totalAmount || 0),
    [credits, buildRsdBreakdown],
  );
  const interestRSDBreakdown = useMemo(
    () => buildRsdBreakdown(credits, (c) => c.interestAmount || 0),
    [credits, buildRsdBreakdown],
  );
  const paidRSDBreakdown = useMemo(
    () => buildRsdBreakdown(credits, (c) => c.paidAmount || 0),
    [credits, buildRsdBreakdown],
  );
  const remainingRSDBreakdown = useMemo(
    () => buildRsdBreakdown(credits, (c) => c.remainingAmount || 0),
    [credits, buildRsdBreakdown],
  );

  // Mobile card rendering
  const renderMobileCard = (credit: Credit) => {
    const totalWithInterest = credit.totalWithInterest || credit.totalAmount;
    const totalPaid = credit.paidAmount + (credit.savedAmount || 0);
    const displayDate = credit.disbursementDate || credit.startDate;

    return (
      <div
        key={credit._id}
        className={`creditMobileCard ${credit.isFrozen ? "frozen" : ""}`}
      >
        <div className="cardHeader">
          <div className="creditName">
            {credit.name}
            {credit.isFrozen && (
              <Tag
                icon={<PauseOutlined />}
                color="default"
                style={{ marginLeft: 8, fontSize: "11px" }}
              >
                {t("Frozen")}
              </Tag>
            )}
          </div>
          <Tag color="blue" style={{ fontSize: 11 }}>
            {credit.currency || "EUR"}
          </Tag>
          <Tag
            className="statusTag"
            color={credit.status === "COMPLETED" ? "green" : "orange"}
          >
            {t(credit.status || "ACTIVE")}
          </Tag>
        </div>

        <div className="cardInfo">
          <div className="infoItem">
            <div className="label">{t("Start")}</div>
            <div className="value">
              {displayDate ? dayjs(displayDate).format("DD.MM.YYYY") : "-"}
            </div>
          </div>

          <div className="infoItem">
            <div className="label">{t("Interest %")}</div>
            <div className="value">
              {credit.interestRate !== undefined
                ? `${Number(credit.interestRate || 0).toFixed(2)}%`
                : "-"}
            </div>
          </div>

          <div className="infoItem">
            <div className="label">{t("Total with interest")}</div>
            <div className="value">
              {totalWithInterest?.toLocaleString()} {currencySymbol}
            </div>
          </div>

          <div className="infoItem">
            <div className="label">{t("Paid")}</div>
            <div className="value success">
              {totalPaid?.toLocaleString()} {currencySymbol}
            </div>
          </div>

          <div className="infoItem">
            <div className="label">{t("Remaining")}</div>
            <div className="value warning">
              {credit.remainingAmount?.toLocaleString()} {currencySymbol}
            </div>
          </div>

          <div className="infoItem">
            <div className="label">{t("Monthly due")}</div>
            <div className="value">
              {calculateDueForMonth(credit, monthFilter)?.toLocaleString()}{" "}
              {currencySymbol}
            </div>
          </div>
        </div>

        <div className="cardProgress">
          {credit.notStarted ? (
            <Tag color="blue" style={{ marginBottom: 4 }}>{t("Not started")}</Tag>
          ) : (
            <>
              <div className="progressLabel">
                <span>{t("Progress")}</span>
                <span className="percentage">{credit.progress || 0}%</span>
              </div>
              <Progress percent={credit.progress || 0} strokeColor="#16a34a" />
            </>
          )}
        </div>

        <div className="cardActions">
          <Button
            type="default"
            icon={<EyeOutlined />}
            onClick={() => loadCreditDetails(credit)}
          >
            {t("View")}
          </Button>
          <Button
            type="primary"
            icon={<DollarOutlined />}
            onClick={() => handleOpenPaymentModal(credit)}
          >
            {t("Pay")}
          </Button>
          <Dropdown
            menu={{
              items: [
                {
                  key: "edit",
                  icon: <EditOutlined />,
                  label: t("Edit"),
                  onClick: () => {
                    setEditingCredit(credit);
                    setFormOpen(true);
                  },
                },
                ...(credit.paymentType === "INSTALLMENTS"
                  ? [
                      {
                        key: "mark",
                        label: t("Mark installment"),
                        disabled: credit.remainingAmount <= 0,
                        onClick: () => handleMarkInstallment(credit._id),
                      },
                    ]
                  : []),
                {
                  type: "divider" as const,
                },
                {
                  key: "freeze",
                  icon: credit.isFrozen ? (
                    <PlayCircleOutlined />
                  ) : (
                    <PauseOutlined />
                  ),
                  label: credit.isFrozen ? t("Unfreeze") : t("Freeze"),
                  onClick: () => handleFreezeCredit(credit, !credit.isFrozen),
                },
                {
                  key: "delete",
                  icon: <DeleteOutlined />,
                  label: (
                    <span style={{ color: "#ef4444" }}>{t("Delete")}</span>
                  ),
                  onClick: () => handleDeleteCredit(credit),
                },
              ],
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </div>
      </div>
    );
  };

  return (
    <div className="creditsPage">
      <Row justify="space-between" align="middle" className="pageHeader">
        <Col>
          <Title level={2}>{t("Credits")}</Title>
          <div className="subtitle">
            {t(
              "Create credit plans, auto-generate monthly installments or track manual payments.",
            )}
          </div>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadCredits}
              loading={loading}
            >
              {t("Refresh")}
            </Button>
            <Button
              icon={<FileImageOutlined />}
              onClick={() => setImageImportOpen(true)}
            >
              {t("Import from image")}
            </Button>
            <Button
              icon={<ScheduleOutlined />}
              onClick={() => setCreditWithRatesOpen(true)}
            >
              {t("New credit with installments")}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingCredit(null);
                setFormOpen(true);
              }}
            >
              {t("New credit")}
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[12, 12]} className="filtersRow">
        <Col xs={24} sm={12} md={8}>
          <Form layout="vertical">
            <Form.Item
              label={t("Filter by location")}
              style={{ marginBottom: 0 }}
            >
              <Select
                allowClear
                placeholder={t("Select location")}
                value={locationFilter || undefined}
                onChange={(val?: string) => setLocationFilter(val || null)}
                options={locations.map((loc) => ({
                  label: loc.name,
                  value: loc._id,
                }))}
              />
            </Form.Item>
          </Form>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form layout="vertical">
            <Form.Item label={t("Month")} style={{ marginBottom: 0 }}>
              <DatePicker
                picker="month"
                style={{ width: "100%" }}
                value={monthFilter || undefined}
                onChange={(val) => setMonthFilter(val)}
              />
            </Form.Item>
          </Form>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form layout="vertical">
            <Form.Item label=" " style={{ marginBottom: 0 }}>
              <Checkbox
                checked={includeFrozen}
                onChange={(e) => setIncludeFrozen(e.target.checked)}
                style={{ opacity: 0.7, fontSize: "13px" }}
              >
                {t("Show frozen credits")}
              </Checkbox>
            </Form.Item>
          </Form>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="statsRow">
        <Col xs={12} sm={12} md={8} lg={6}>
          <CurrencyStatCard
            title={t("Total credit volume")}
            isRSDBase={isRSDBase}
            breakdown={totalRSDBreakdown}
            rawValue={stats.total}
            currencySymbol={currencySymbol}
            rawPrefix
            isMobile={isTouchStatsLayout}
            t={t}
          />
        </Col>
        <Col xs={12} sm={12} md={8} lg={6}>
          <CurrencyStatCard
            title={t("Monthly due (selected month)")}
            isRSDBase={isRSDBase}
            breakdown={monthlyDueRSDBreakdown}
            rawValue={monthlyDue}
            currencySymbol={currencySymbol}
            color="#dc2626"
            isMobile={isTouchStatsLayout}
            t={t}
          />
        </Col>
        <Col xs={12} sm={12} md={8} lg={6}>
          <CurrencyStatCard
            title={t("Total interest")}
            isRSDBase={isRSDBase}
            breakdown={interestRSDBreakdown}
            rawValue={stats.interest}
            currencySymbol={currencySymbol}
            color="#7c3aed"
            isMobile={isTouchStatsLayout}
            t={t}
          />
        </Col>
        <Col xs={12} sm={12} md={8} lg={6}>
          <CurrencyStatCard
            title={t("Paid so far")}
            isRSDBase={isRSDBase}
            breakdown={paidRSDBreakdown}
            rawValue={stats.paid}
            currencySymbol={currencySymbol}
            color="#16a34a"
            isMobile={isTouchStatsLayout}
            t={t}
          />
        </Col>
        <Col xs={12} sm={12} md={8} lg={6}>
          <CurrencyStatCard
            title={t("Remaining")}
            isRSDBase={isRSDBase}
            breakdown={remainingRSDBreakdown}
            rawValue={stats.remaining}
            currencySymbol={currencySymbol}
            color="#f97316"
            isMobile={isTouchStatsLayout}
            t={t}
          />
        </Col>
        <Col xs={12} sm={12} md={8} lg={6}>
          <Card>
            <Statistic
              title={t("Active credits")}
              value={stats.active}
              valueStyle={{ color: "#2563eb" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Desktop table view */}
      {!isMobile && (
        <Card className="desktopTable">
          <Table
            loading={loading}
            dataSource={filteredCredits}
            rowKey={(record) => record._id}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1400 }}
            rowClassName={(record) =>
              record.isFrozen ? "frozen-credit-row" : ""
            }
            columns={[
              {
                title: t("Credit name"),
                dataIndex: "name",
                width: 200,
                fixed: "left",
                ellipsis: true,
                render: (text: string, record: Credit) => (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      width: "100%",
                    }}
                  >
                    <Tooltip title={text} placement="topLeft">
                      <Button
                        type="link"
                        onClick={() => loadCreditDetails(record)}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          padding: "4px 8px",
                          height: "auto",
                          textAlign: "left",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <FileSearchOutlined style={{ flexShrink: 0 }} />
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {text}
                        </span>
                      </Button>
                    </Tooltip>
                    {record.isFrozen && (
                      <Tag
                        icon={<PauseOutlined />}
                        color="default"
                        style={{
                          fontSize: "11px",
                          padding: "0 4px",
                          flexShrink: 0,
                        }}
                      >
                        {t("Frozen")}
                      </Tag>
                    )}
                  </div>
                ),
              },
              {
                title: t("Start"),
                dataIndex: "startDate",
                width: 110,
                render: (value: string, record: Credit) => {
                  // Prikaži disbursementDate ako postoji, inače startDate
                  const displayDate = record.disbursementDate || value;
                  return displayDate
                    ? dayjs(displayDate).format("DD.MM.YYYY")
                    : "-";
                },
              },
              {
                title: t("Total"),
                dataIndex: "totalAmount",
                width: 120,
                render: (val: number) => val?.toLocaleString(),
                sorter: (a: Credit, b: Credit) => a.totalAmount - b.totalAmount,
              },
              {
                title: t("Interest %"),
                dataIndex: "interestRate",
                width: 100,
                render: (val?: number) =>
                  val !== undefined ? `${Number(val || 0).toFixed(2)}%` : "-",
                sorter: (a: Credit, b: Credit) =>
                  (a.interestRate || 0) - (b.interestRate || 0),
              },
              {
                title: t("Total with interest"),
                dataIndex: "totalWithInterest",
                width: 140,
                render: (_: any, record: Credit) => {
                  const total = record.totalWithInterest || record.totalAmount;
                  const invalid =
                    record.totalWithInterest &&
                    record.totalWithInterest < record.totalAmount;
                  return (
                    <span style={{ color: invalid ? "#ff4d4f" : undefined }}>
                      {total?.toLocaleString()}
                      {invalid && (
                        <Tooltip
                          title={t(
                            "Total with interest is less than principal - invalid data",
                          )}
                        >
                          {" ⚠️"}
                        </Tooltip>
                      )}
                    </span>
                  );
                },
                sorter: (a: Credit, b: Credit) =>
                  (a.totalWithInterest || a.totalAmount) -
                  (b.totalWithInterest || b.totalAmount),
              },
              {
                title: t("Paid"),
                dataIndex: "paidAmount",
                width: 120,
                render: (val: number, record: Credit) => {
                  const totalPaid = val + (record.savedAmount || 0);
                  const overpaid =
                    totalPaid >
                    (record.totalWithInterest || record.totalAmount);
                  return (
                    <span style={{ color: overpaid ? "#ff4d4f" : undefined }}>
                      {totalPaid?.toLocaleString()}
                      {overpaid && (
                        <Tooltip title={t("Paid amount exceeds total amount")}>
                          {" ⚠️"}
                        </Tooltip>
                      )}
                    </span>
                  );
                },
                sorter: (a: Credit, b: Credit) => {
                  const totalA = a.paidAmount + (a.savedAmount || 0);
                  const totalB = b.paidAmount + (b.savedAmount || 0);
                  return totalA - totalB;
                },
              },
              {
                title: t("Remaining"),
                dataIndex: "remainingAmount",
                width: 120,
                render: (val: number) => val?.toLocaleString(),
                sorter: (a: Credit, b: Credit) =>
                  a.remainingAmount - b.remainingAmount,
              },
              // Sakrivene kolone: Mode i Type
              // {
              //   title: t("Mode"),
              //   dataIndex: "paymentMode",
              //   render: (val: string) => (
              //     <Tag color={val === "AUTO" ? "blue" : "default"}>
              //       {val === "AUTO" ? t("Auto") : t("Manual")}
              //     </Tag>
              //   ),
              // },
              // {
              //   title: t("Type"),
              //   dataIndex: "paymentType",
              //   render: (val: string) => (
              //     <Tag color={val === "INSTALLMENTS" ? "purple" : "gold"}>
              //       {val === "INSTALLMENTS" ? t("Installments") : t("Manual")}
              //     </Tag>
              //   ),
              // },
              {
                title: t("Progress"),
                dataIndex: "progress",
                width: 150,
                render: (_: any, record: Credit) => (
                  <Progress percent={record.progress || 0} size="small" />
                ),
              },
              {
                title: t("Status"),
                dataIndex: "status",
                width: 100,
                render: (val: string) => (
                  <Tag color={val === "COMPLETED" ? "green" : "orange"}>
                    {t(val)}
                  </Tag>
                ),
              },
              {
                title: t("Confirm Payment"),
                align: "center" as const,
                width: 140,
                render: (_: any, record: Credit) => {
                  // Get unpaid installments that are due
                  const unpaidDue =
                    record.payments?.filter((p: CreditPayment) => {
                      const isDue = dayjs(p.paymentDate).isSameOrBefore(
                        dayjs(),
                        "day",
                      );
                      return !p.isPaid && isDue;
                    }) || [];

                  if (record.notStarted) {
                    return <Tag color="blue">{t("Not started")}</Tag>;
                  }

                  if (unpaidDue.length === 0) {
                    return <Tag color="success">{t("Up to date")}</Tag>;
                  }

                  if (unpaidDue.length === 1) {
                    // Single checkbox for one payment
                    const payment = unpaidDue[0];
                    return (
                      <Tooltip
                        title={`${t("Confirm payment received")}: ${dayjs(payment.paymentDate).format("DD.MM.YYYY")} - ${payment.amount?.toLocaleString()}`}
                      >
                        <Checkbox
                          key={`${payment._id}-${payment.isPaid}`}
                          checked={payment.isPaid || false}
                          onChange={(e) => {
                            if (e.target.checked) {
                              handleConfirmPayment(
                                record._id,
                                payment._id,
                                true,
                              );
                            }
                          }}
                        >
                          <Tag color="error">{unpaidDue.length}</Tag>
                        </Checkbox>
                      </Tooltip>
                    );
                  }

                  // Multiple unpaid - show popover with list
                  const content = (
                    <div
                      style={{
                        maxWidth: 300,
                        maxHeight: 300,
                        overflow: "auto",
                      }}
                    >
                      <Space direction="vertical" style={{ width: "100%" }}>
                        {unpaidDue.map((payment: CreditPayment) => (
                          <div
                            key={payment._id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <Checkbox
                              key={`${payment._id}-${payment.isPaid}`}
                              checked={payment.isPaid || false}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  handleConfirmPayment(
                                    record._id,
                                    payment._id,
                                    true,
                                  );
                                }
                              }}
                            />
                            <span style={{ fontSize: 12 }}>
                              {dayjs(payment.paymentDate).format("DD.MM")} -{" "}
                              <strong>
                                {payment.amount?.toLocaleString()}
                              </strong>
                            </span>
                          </div>
                        ))}
                      </Space>
                    </div>
                  );

                  return (
                    <Popover
                      content={content}
                      title={t("Confirm received payments")}
                      trigger="click"
                    >
                      <Button size="small" danger>
                        <CheckCircleOutlined /> {unpaidDue.length}
                      </Button>
                    </Popover>
                  );
                },
              },
              {
                title: t("Actions"),
                width: 80,
                fixed: "right",
                render: (_: any, record: Credit) => {
                  const menuItems = [
                    {
                      key: "preview",
                      icon: <EyeOutlined />,
                      label: t("Preview"),
                      onClick: () => loadCreditDetails(record),
                    },
                    {
                      key: "edit",
                      icon: <EditOutlined />,
                      label: t("Edit"),
                      onClick: () => {
                        setEditingCredit(record);
                        setFormOpen(true);
                      },
                    },
                    {
                      key: "manual",
                      label: t("Manual payment"),
                      onClick: () => handleOpenPaymentModal(record),
                    },
                    ...(record.paymentType === "INSTALLMENTS"
                      ? [
                          {
                            key: "mark",
                            label: t("Mark installment"),
                            disabled: record.remainingAmount <= 0,
                            onClick: () => handleMarkInstallment(record._id),
                          },
                        ]
                      : []),
                    {
                      type: "divider" as const,
                    },
                    {
                      key: "freeze",
                      icon: record.isFrozen ? (
                        <PlayCircleOutlined />
                      ) : (
                        <PauseOutlined />
                      ),
                      label: record.isFrozen ? t("Unfreeze") : t("Freeze"),
                      onClick: () =>
                        handleFreezeCredit(record, !record.isFrozen),
                    },
                    {
                      key: "delete",
                      icon: <DeleteOutlined />,
                      label: (
                        <span style={{ color: "#ef4444" }}>{t("Delete")}</span>
                      ),
                      onClick: () => handleDeleteCredit(record),
                    },
                  ];

                  return (
                    <Dropdown
                      menu={{ items: menuItems }}
                      trigger={["click"]}
                      placement="bottomRight"
                    >
                      <Button
                        type="text"
                        size="small"
                        shape="circle"
                        icon={<MoreOutlined />}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      />
                    </Dropdown>
                  );
                },
              },
            ]}
          />
        </Card>
      )}

      {/* Mobile cards view */}
      {isMobile && (
        <div className="mobileCards creditsList">
          {loading ? (
            <div className="loadingState">
              <Empty description={t("Loading...")} />
            </div>
          ) : filteredCredits.length === 0 ? (
            <div className="emptyState">
              <Empty
                description={t("No credits found. Create your first credit.")}
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setEditingCredit(null);
                    setFormOpen(true);
                  }}
                >
                  {t("New credit")}
                </Button>
              </Empty>
            </div>
          ) : (
            filteredCredits.map((credit) => renderMobileCard(credit))
          )}
        </div>
      )}

      <CreditFormModal
        open={formOpen}
        loading={savingCredit}
        credit={editingCredit}
        locations={locations}
        onCancel={() => {
          setFormOpen(false);
          setEditingCredit(null);
        }}
        onSubmit={handleSaveCredit}
      />

      <CreditImageImportModal
        open={imageImportOpen}
        clientId={clientId}
        token={token}
        onCancel={() => setImageImportOpen(false)}
        onSuccess={(credit) => {
          upsertCredit(credit);
          setImageImportOpen(false);
        }}
      />

      <CreditDetailsDrawer
        open={detailsOpen}
        loading={detailsLoading}
        credit={selectedCredit}
        onClose={() => setDetailsOpen(false)}
        onEditCredit={(credit) => {
          setEditingCredit(credit);
          setFormOpen(true);
        }}
        onAddPayment={(credit, payment) =>
          handleOpenPaymentModal(credit, payment)
        }
        onMarkInstallment={handleMarkInstallment}
        onDeletePayment={handleDeletePayment}
        onConfirmPayment={handleConfirmPayment}
        onRefreshCredit={async () => {
          if (selectedCredit) {
            await loadCreditDetails(selectedCredit);
            await loadCredits();
          }
        }}
        onCreditUpdated={(updated) => {
          upsertCredit(updated);
          setSelectedCredit(updated);
        }}
      />

      <Modal
        open={paymentModal.open}
        title={t("Edit manual payment")}
        onCancel={() => setPaymentModal({ open: false })}
        onOk={handleSavePayment}
        okButtonProps={{ loading: savingPayment }}
      >
        <Form layout="vertical" form={paymentForm}>
          <Form.Item
            name="amount"
            label={t("Amount")}
            rules={[{ required: true, message: t("Enter amount") }]}
          >
            <InputNumber style={{ width: "100%" }} min={1} step={50} />
          </Form.Item>
          <Form.Item
            name="paymentDate"
            label={t("Payment date")}
            rules={[{ required: true, message: t("Select date") }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="note" label={t("Note")}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <ManualPaymentsModal
        open={bulkPaymentModal.open}
        credit={bulkPaymentModal.credit}
        saving={savingBulkPayment}
        onCancel={() => setBulkPaymentModal({ open: false })}
        onSave={handleSaveBulkPayments}
      />

      <CreditWithRatesModal
        open={creditWithRatesOpen}
        loading={savingCreditWithRates}
        locations={locations}
        onCancel={() => setCreditWithRatesOpen(false)}
        onSubmit={handleCreateCreditWithRates}
      />
    </div>
  );
};

export default Credits;
